﻿/* global BABYLON */
import { UpdateMaterialImageVisitor } from '../UpdateMaterialImageVisitor.js';
import { ChildVisitOrder, ProductDrawingVisitor } from '../ProductVisitors.js';
import { FlexiBasketBuilder, MeshBasketBuilder } from './BabylonBasketBuilders.js';
//import { AxisBuilder } from './BabylonAxisBuilder.js';

export class BabylonRenderVisitor extends ProductDrawingVisitor {
  constructor(finishResources, drawingElement, scene) {
    super();
    this.finishResources = finishResources;
    this.drawingElement = drawingElement;
    this.scene = scene;
    // The visitor wants to control when the children are visited:
    this.childVisitOrder = ChildVisitOrder.noChildren;
    /** When isInlineUpdate is true, the component structure has not changed and there's no need to
     * recreate any of the html elements. Instead each element is updated inline. */
    this.isInlineUpdate = false;
    this.vector2 = new BABYLON.Vector3(2, 2, 2); // Constant reused in the mesh positioning code
  }

  //#region *** Materials ***

  static lookupColorMaterial(component, scene) {
    const compMatr = component.material;
    // There is one color material per scene in the compMatr object
    const sceneId = scene.name;
    compMatr.BLColorMaterials = compMatr.BLColorMaterials || {};
    let material = compMatr.BLColorMaterials[sceneId];
    if (!material) {
      material = new BABYLON.StandardMaterial(compMatr.id, scene);
      material.diffuseColor = BABYLON.Color3.FromHexString(compMatr.color);
      if (compMatr.specularColor) {
        material.specularColor = BABYLON.Color3.FromHexString(compMatr.specularColor);
      }
      material.freeze(); // Comment this if you need to debug the material in the BJS Inspector
      compMatr.BLColorMaterials[sceneId] = material;
      //console.log(`%cCreated material ${material.id} for ${sceneId}`, 'color: green');
    }
    return material;
  }

  static lookupMaterial(component, scene, needsImageMaterial) {
    const compMatr = component.material;
    if (compMatr.isColor && !needsImageMaterial) {
      return BabylonRenderVisitor.lookupColorMaterial(component, scene);
    }
    if (compMatr.current) {
      const currentImg = compMatr.current;
      const resolution = currentImg.resolution;

      // There is one material per scene in the currentImg object
      const sceneId = scene.name;
      currentImg.BLMaterials = currentImg.BLMaterials || {};

      let material = currentImg.BLMaterials[sceneId];
      if (!material) {
        const texture = new BABYLON.Texture(currentImg.url, scene);
        if (compMatr.rotation) {
          texture.wAng = BABYLON.Tools.ToRadians(compMatr.rotation);
        }
        material = new BABYLON.StandardMaterial(`${compMatr.id}-${resolution}`, scene);
        material.diffuseTexture = texture;

        if (compMatr.hasAlpha === true) {
          /* These assignments were chosen by studying the
           * https://doc.babylonjs.com/features/featuresDeepDive/materials/advanced/transparent_rendering
           * article, and our own trial and error for how to get the best display quality and performance
           * for mesh baskets. 4/18/2023. */
          material.diffuseTexture.hasAlpha = true;
          material.backFaceCulling = true;
          material.useAlphaFromDiffuseTexture = true;
          //material.needDepthPrePass = true;
        }
        material.freeze();

        currentImg.BLMaterials[sceneId] = material;
        //console.log(`%cCreated material ${material.id} for ${sceneId}`, 'color: green');
      }
      return material;
    } else {
      console.warn(`Material ${compMatr.id} was found, but it's image has not been loaded`);
    }
  }

  // Not used?
  findMaterial(component) {
    return BabylonRenderVisitor.lookupMaterial(component, this.scene, /* needsImageMaterial: */ true);
  }

  /**
   * Replaces the mesh's (component.displayObject's) material based on the properties of
   * component.material (which is our own material type, not Babylon's). The method is async because
   * of the async onJitLoadMaterial callback which will await until the image is loaded.
   * @param {callback} onJitLoadMaterial (async) Will be called if the component's material image has not been
   * loaded yet. It's preferred to preload the material images, but sometimes new product finishes
   * are assigned (from the server-side) before the client's preload code has been updated.
   * @param {object} mesh This parameter allows us to update the display object (mesh) before it has been
   * assigned to the component. If the parameter is not used, then component.displayObject is used.
   * @param {bool} needsImageMaterial Some materials, e.g. MeshBasket, uses both an image and a color
   * for its rendering. In these cases, the needsImageMaterial parameter must be set to true for the
   * image material to be used.
   */
  static async replaceMeshMaterialStatic(component, onJitLoadMaterial, mesh, scene, needsImageMaterial) {
    scene = scene || component.drawingService.scene;
    mesh = mesh || component.displayObject;
    if (!mesh) {
      //throw Error('Mesh was unassigned');
      /* It's possible this method was called before the component was added to the drawing for the
       * first time. Let's just return now. */
      return;
    }
    if (component.material) {
      if (component.material.isColor && !needsImageMaterial) {
        mesh.material = BabylonRenderVisitor.lookupColorMaterial(component, scene);
      } else if (component.material.isImage) {
        const currentImg = component.material.current;
        if (currentImg) {
          if (currentImg.url) {
            mesh.material = BabylonRenderVisitor.lookupMaterial(component, scene, needsImageMaterial);
          } else {
            console.warn(`Not sure what happened here; the image.url was undefined on material ${component.material.id}`);
          }
        } else {
          /* This probably means the product factory didn't list the material as one in use by the
           * product (see ProductViewModelFactory.getAllKnownProductMaterialIds() in subclass). */
          if (onJitLoadMaterial) {
            /* This async callback will await until the low-res image has been loaded, and later,
             * after the hi-res image is loaded, the "inner" callback will be executed. So, we update
             * the mesh.material twice, first for the lo-res image, and later for the hi-res image. */
            await onJitLoadMaterial(component.material, () => {
              //console.log('Updating url to use the high-res image');
              // Recursive call, but we make sure the onJitLoadMaterial parameter is null to avoid stack overflow:
              BabylonRenderVisitor.replaceMeshMaterialStatic(component, null, mesh, scene, needsImageMaterial);
            });
            //console.log('Setting background image url to use low-res image');
            // Recursive call, but we make sure the onJitLoadMaterial parameter is null to avoid stack overflow:
            BabylonRenderVisitor.replaceMeshMaterialStatic(component, null, mesh, scene, needsImageMaterial);
          } else {
            console.warn(`Material ${component.material.id} was found, but it's image has not been loaded`);
          }
        }
      }
    }
  }

  /**
   * Replaces the mesh's (component.displayObject's) material based on the properties of
   * component.material (which is our own material type, not Babylon's). The method is async because
   * of the async this.onJitLoadMaterial callback which will await until the image is loaded.
   * @param {any} mesh This parameter allows us to update the display object (mesh) before it has been
   * assigned to the component. If the parameter is not used, then component.displayObject is used.
   */
  async replaceMeshMaterial(component, mesh, needsImageMaterial) {
    // Note, it's the user's (owner's) responsibility to assign the this.onJitLoadMaterial callback.
    await BabylonRenderVisitor.replaceMeshMaterialStatic(component,
      async (material, onHisResImgLoaded) => await this.onJitLoadMaterial(material, onHisResImgLoaded),
      mesh, this.scene, needsImageMaterial);
  }

  //#endregion *** Materials ***

  //#region *** Positioning & Resizing Meshes ***

  /**
   * Moves the Lower/Bottom/Back position (LBB) of the mesh to the LBB of the parent mesh
   * (or world if no parent).
   */
  moveLbbToParentLbb(mesh, relativePosition = null) {
    /* This method/algorithm was developed in the Babylon Playground: https://playground.babylonjs.com/#PTR26K#45
     * I also started the outline of a blog article on the subject: https://dev.to/tormnator/positioning-sub-meshes-in-babylon-js-7eh-temp-slug-1296673?preview=32eaa4260ae64e5420decb924e140a00a2c2ed1f48de4d6ba34bd8d2f9b7ae14ea505f0c204009a8a7cf08eefbc5b0c449e46e38a403806c7b22618d
     */
    /* TODO: Once we trust this method is bug free we can make performance optimizations (but keep
     * the original code also). */

    // **** The first segment below is functionally equivalent to the moveLbfToParentLbf method. ****

    if (mesh.parent) {
      /* Note: In Babylon v4 the force parameter was not needed in order to compute the correct
       * world matrix, but in v5 the function would not take rotations into consideration. The
       * force: true parameter fixes that. 4/18/2023. */
      mesh.parent.computeWorldMatrix(/*force:*/ true);
    }
    mesh.computeWorldMatrix(/*force:*/ true);
    let bbox = mesh.getBoundingInfo().boundingBox;
    let min = bbox.minimumWorld;
    let max = bbox.maximumWorld;
    let size = max.subtract(min); // The size of the mesh
    const meshDepth = size.z;
    // The distance from the local origin to the Left/Bottom/Front position:
    let lbf = size.divide(this.vector2);
    // This position places the LBF of the child mesh in the local origin of the parent mesh (or world if no parent):
    let position = lbf;
    //console.log(`Inner box lbf to parent origin x: ${position.x}`);
    let parentDepth;
    if (mesh.parent) {
      // Now we need to position the child mesh in the LBF of the parent mesh:
      bbox = mesh.parent.getBoundingInfo().boundingBox;
      min = bbox.minimumWorld;
      max = bbox.maximumWorld;
      size = max.subtract(min); // The size of the parent mesh
      parentDepth = size.z;
      // The distance from the local origin of the parent mesh to the Left/bottom/front position:
      lbf = size.divide(this.vector2);
      const position2 = lbf;
      position.subtractInPlace(position2);
      // We also have take the local scale into consideration:
      position.divideInPlace(mesh.parent.scaling);
    }
    mesh.position = relativePosition ? position.add(relativePosition) : position;

    // **** The code above is functionally equivalent to the moveLbfToParentLbf method. ****


    /* Now that we have the mesh in the LBF position, lets move the child mesh into the opposite
     * side of the X-axis (e.g. from positive Z to negative Z). */

    if (mesh.parent) {
      const delta = parentDepth - meshDepth;
      mesh.position.z += delta;
      if (relativePosition) {
        /* Move the mesh to a new position relative to the position of the parent.
         * We multiply by two because we first have to undo what the code above did,
         * then move it again. */
        mesh.position.z -= (relativePosition.z * 2);
      }
    } else { // Just move the mesh to the opposite side of the x-axis:
      mesh.position.z = -mesh.position.z;
    }
  }

  /**
   * Sets the child component's mesh position based on 1) the child component's position in the
   * product, and 2) the fact that in Babylon every mesh's (0,0,0) position is the local origin in
   * the 3D center of the mesh, and that the child origin is placed on top of the parent's origin.
   * @param {any} childComp Child product component
   */
  setChildPosition(childComp) {
    let { left, bottom, back } = childComp.rect; // The left, bottom, and back positions of the product component:
    const relPosition = new BABYLON.Vector3(left, bottom, back);
    this.moveLbbToParentLbb(childComp.displayObject, relPosition);
  }

  setMeshRotation(mesh, rotation) {
    let x, y, z;
    if (mesh.metadata && mesh.metadata.intrinsicRotation) {
      /* intrinsicRotation is set in the createRod method because the Babylon cylinder mesh is
       * vertical by default and we need it to be horizontal. */
      ({ x, y, z } = mesh.metadata.intrinsicRotation);
    } else {
      x = y = z = 0;
    }
    mesh.rotation.x = BABYLON.Tools.ToRadians(x + rotation.x);
    mesh.rotation.y = BABYLON.Tools.ToRadians(y + rotation.y);
    mesh.rotation.z = BABYLON.Tools.ToRadians(z + rotation.z);
  }

  setDefaultMeshRotation(component) {
    const rotation = this.getRotation(component);
    this.setMeshRotation(component.displayObject, rotation);
  }

  visitAndUpdatePosition(component) {
    const compMesh = this.visit(component);
    if (compMesh) {
      // With the new positioning algorithm we must set rotation before we do the positioning:
      this.setDefaultMeshRotation(component);

      const parent = component.parent;
      if (parent) {
        this.setChildPosition(component);
      }
      //this.setDefaultMeshRotation(component);
    }
    return compMesh;
  }

  //#endregion *** Positioning & Resizing Meshes ***

  //#region *** Mesh Helper Methods ***

  /** Creates a empty BABYLON.Mesh without any vertices or materials. */
  createEmptyMesh(name) {
    return new BABYLON.Mesh(name, this.scene);
  }

  /** Adds the childMesh as a child of the current mesh (stored in currentContext.data). */
  addChildMesh(childMesh) {
    /* It's a bit strange, but we'll allow calling this method without having a parent mesh to
     * add the child mesh to. The original use case is to create isolated meshes which
     * will later be added to the drawing (using the BabylonDrawingService methods). */
    if (this.currentContext) {
      childMesh.parent = this.currentContext.data;
    }
    return childMesh;
  }

  //[Obsolete('Not currently used, but might be used (again) in the future')]
  /**
   * Creates a empty BABYLON.Mesh without any vertices or materials. If component is assigned, then
   * we'else assign the mesh to the component's displayObject property. If we have a current context,
   * then we'll also add the mesh to the parent mesh (currentContext.data).
   */
  addEmptyMesh(component, name) {
    const newMesh = this.createEmptyMesh(name || (component ? component.name : null));
    if (component) {
      component.displayObject = newMesh;
    }
    return this.addChildMesh(newMesh);
  }

  /**
   * Looks at this.isInlineUpdate to determine whether to create a new mesh or reuse the
   * component's displayObject. If creating a new mesh, then an empty BABYLON.Mesh without any
   * vertices or materials will be created.
   * @param {bool} pushContext true/false; if true, the element will be pushed onto the context stack.
   * @param {function} onLookupMesh If this.isInlineUpdate and component is unassigned, then use the
   *   onLookupMesh callback to acquire the mesh.
   */
  getEmptyMeshAndPushContext(component, name, pushContext, onLookupMesh) {
    let mesh;
    if (this.isInlineUpdate) {
      mesh = component && component.displayObject ? component.displayObject : onLookupMesh(component, name);
    } else {
      mesh = this.addEmptyMesh(component, name);
    }
    if (pushContext) {
      this.pushContext(mesh);
    }
    return mesh;
  }

  /**
   * Looks at this.isInlineUpdate to determine whether to create a new mesh or reuse the
   * component's displayObject. If creating a new mesh, then a Box Mesh with vertices, but without
   * any materials, will be created.
   * @param {bool} pushContext true/false; if true, the element will be pushed onto the context stack.
   * @param {function} onLookupMesh If this.isInlineUpdate and component is unassigned, then use the
   *   onLookupMesh callback to acquire the mesh.
   */
  getContainerMeshAndPushContext(component, name, pushContext, onLookupMesh) {
    let mesh;
    if (this.isInlineUpdate) {
      mesh = component && component.displayObject ? component.displayObject : onLookupMesh(component, name);
    } else {
      mesh = this.createBox(component, name, /*isTransparent:*/ true);
      // If we don't want to create meshes with vertices:
      //mesh = this.addEmptyMesh(component, name);
    }
    if (pushContext) {
      this.pushContext(mesh);
    }
    return mesh;
  }

  getChildMeshByName(mesh, name, recursive) {
    const meshes = mesh.getChildMeshes(!recursive, node => node.name === name);
    return meshes.length > 0 ? meshes[0] : null;
  }

  //#endregion *** Mesh Helper Methods ***


  //#region *** Mesh Creation Methods ***

  createBox(component, name, isTransparent = false, onGetFaceUV = null) {
    const compRect = component.rect;
    let mesh;
    if (this.isInlineUpdate) {
      mesh = component.displayObject;
      // TODO: Update size
    } else {
      let faceUV;
      if (!isTransparent) {
        const widthRatio = component.getMaterialWidthRatio();
        const heightRatio = component.getMaterialHeightRatio();
        const depthRatio = component.getMaterialDepthRatio();
        if (onGetFaceUV) {
          faceUV = onGetFaceUV(widthRatio, heightRatio, depthRatio);
        } else {
          faceUV = [];
          // TODO: Consider the product material's width- and heightStrategy here
          faceUV[0] = new BABYLON.Vector4(0.0, 0.0, widthRatio, heightRatio); // Rear face
          faceUV[1] = new BABYLON.Vector4(0.0, 0.0, widthRatio, heightRatio); // Front face
          faceUV[2] = new BABYLON.Vector4(0.0, 0.0, depthRatio, heightRatio); // Right side
          faceUV[3] = new BABYLON.Vector4(0.0, 0.0, depthRatio, heightRatio); // Left side
          faceUV[4] = new BABYLON.Vector4(0.0, 0.0, widthRatio, depthRatio);  // On top
          faceUV[5] = new BABYLON.Vector4(0.0, 0.0, widthRatio, depthRatio);  // At bottom
        }
      }

      mesh = BABYLON.MeshBuilder.CreateBox(name || component.name, {
        width: compRect.width,
        height: compRect.height,
        depth: compRect.depth,
        faceUV: faceUV,
        //topBaseAt: options.topBaseAt,
        //bottomBaseAt: options.bottomBaseAt,
        wrap: true,
        updatable: true
      }, this.scene);
      mesh.isPickable = false;
      component.displayObject = mesh;
      //console.log(`Created ${mesh.name} box for scene ${this.scene.name}`);
    }
    if (isTransparent) {
      let material = this.scene.getMaterialByID('Transparent');
      if (!material) {
        material = new BABYLON.StandardMaterial('Transparent', this.scene);
        material.alpha = 0;
        material.freeze();
      }
      mesh.material = material;
    } else {
      /* await */ this.replaceMeshMaterial(component, mesh);
    }
    if (!mesh.parent) {
      this.addChildMesh(mesh);
    }
    return mesh;
  }

  createRod(component, isSupportRod) {
    const compRect = component.rect;
    let mesh;
    if (this.isInlineUpdate) {
      mesh = component.displayObject;
      // TODO: Update size
    } else {
      mesh = new BABYLON.MeshBuilder.CreateCylinder(component.name, {
        diameter: compRect.depth,
        //subdivisions: 6,
        tessellation: 6,
        height: isSupportRod ? compRect.height : compRect.width,
      }, this.scene);
      if (!isSupportRod) {
        // Cylinders are vertical by default; we want them horizontal. We'd like to make it simple:
        //mesh.rotation.z = Math.PI / 2;
        /* ... but rotation will be overwritten later by our code, so we need to save it for now,
         * and apply it later. */
        mesh.metadata = mesh.metadata || {};
        mesh.metadata.intrinsicRotation = new BABYLON.Vector3(0, 0, 90);
      }
      mesh.isPickable = false;
      component.displayObject = mesh;
    }
    /* await */ this.replaceMeshMaterial(component, mesh);
    if (!mesh.parent) {
      this.addChildMesh(mesh);
    }
    return mesh;
  }

  /*async*/ createFlexiBasket(component) {
    let mesh;
    if (this.isInlineUpdate) {
      mesh = component.displayObject;
      // TODO: Update size
    } else {
      if (!this.basketBuilder) {
        this.basketBuilder = new FlexiBasketBuilder(this.scene);
      }
      const { width, height, depth } = component.rect;
      mesh = this.basketBuilder.addBasket(component.name || 'FlexiBasket', width / 10, depth / 10, height / 10, null, 10);
      component.displayObject = mesh;
    }

    // WARNING: The code below should be kept in sync with the code in BabylonUpdateMaterialImageVisitor.visitFlexiBasketAndTracks()
    const wireMeshes = mesh.getChildMeshes(true);
    /* await */ this.replaceMeshMaterial(component, wireMeshes[0]);

    if (!mesh.parent) {
      this.addChildMesh(mesh);
    }
    return mesh;
  }

  /*async*/ createMeshBasket(component) {
    let mesh;
    if (this.isInlineUpdate) {
      mesh = component.displayObject;
      // TODO: Update size
    } else {
      if (!this.meshBasketBuilder) {
        this.meshBasketBuilder = new MeshBasketBuilder(this.scene);
      }
      const compRect = component.rect;
      mesh = this.meshBasketBuilder.addBasket(component.name || 'MeshBasket', compRect);
      component.displayObject = mesh;
      //console.log(`Created ${mesh.name} mesh basket for scene ${this.scene.name}`); // TODO: Remove

      //const axes = new BABYLON.AxesViewer(this.scene, component.rect.width); // TODO: Debug!!!!

      // Our own helper class below (looks better than Babylon's built-in axis function)
      //const axisBuilder = new AxisBuilder();
      //axisBuilder.showAxes(50, scene);
    }

    // WARNING: The code below should be kept in sync with the code in BabylonUpdateMaterialImageVisitor.visitMeshBasketAndTracks()
    const childMeshes = mesh.getChildMeshes(true);
    /*await*/ this.replaceMeshMaterial(component, childMeshes[0], /* needsImageMaterial: */ true); // Basket
    /*await*/ this.replaceMeshMaterial(component, childMeshes[1]); // Rim

    if (!mesh.parent) {
      this.addChildMesh(mesh);
    }
    return mesh;
  }

  createPanel(panel, name, onGetFaceUV) {
    return this.createBox(panel, name, false, onGetFaceUV);
  }

  createFloorModule(module, onVisitContents) {
    const moduleMesh = this.getContainerMeshAndPushContext(module, null, true);

    const { leftSide, rightSide } = module;
    if (leftSide) {
      this.visitAndUpdatePosition(leftSide);
    }

    onVisitContents(module, moduleMesh);

    if (rightSide) {
      this.visitAndUpdatePosition(rightSide);
    }

    this.popContext();
    return moduleMesh;
  }

  createStandardModule(module, beforeVisitContents) {
    // The createFloorModule method will render any flexi-sides. Anything in between should be
    // created in the callback below.
    const moduleMesh = this.createFloorModule(module,
      (mdule, mduleMesh) => {
        /* We are now in between the flexi-sides (if any). Let's create a parent mesh for each floor
         * module and other content. First let's create a fake component with the size of the mesh: */
        const contentsComp = {
          rect: {
            left: 0,
            bottom: 0,
            back: 0,
            width: mdule.rect.width,
            height: mdule.rect.height,
            depth: mdule.rect.depth,
            rotation: { x: 0, y: 0, z: 0 }
          },
        };
        const contentsMesh = this.getContainerMeshAndPushContext(contentsComp, 'std-module-contents', true,
          (comp, classes) => {
            /* (when this.isInlineUpdate == true) because this mesh is not associated with any
             * component, we can't lookup the mesh from component.displayObject. So we must do it
             * manually here: */
            return this.getChildMeshByName(mduleMesh, 'std-module-contents');
          });
        /* The std.module's contents are always in position (0,0,0), even if the module has flexi-sides.
         * The floor modules will have their left position adjusted for the flexiside's thickness.
         * Hence, we don't need to set any position here !!! */
        //this.setChildPosition(contentsComp);
        if (beforeVisitContents) {
          // This is where hangrods are created for a HangRodModule, and the TopShelfModule in an Organizer:
          beforeVisitContents(mdule, contentsMesh);
        }

        mdule.floorModules.forEach(floorModule => this.visitAndUpdatePosition(floorModule));
        mdule.otherComponents.forEach(component => this.visitAndUpdatePosition(component));
        this.popContext(); // moduleMesh
      });
    return moduleMesh;
  }

  //#endregion *** Mesh Creation Methods ***

  //#region *** Main Visitation Methods ***

  visitSideWall(sideWall) {
    return this.createPanel(sideWall);
  }

  visitFlexiSide(flexiSide) {
    return this.createPanel(flexiSide);
  }

  visitHangRodWithBrackets(hangRod) {
    return this.createRod(hangRod);
  }

  visitSupportRodWithBrackets(supportRod) {
    return this.createRod(supportRod, /*isSupportRod:*/ true);
  }

  visitFlexiBasketAndTracks(basket) {
    return this.createFlexiBasket(basket);
  }

  visitMeshBasketAndTracks(basket) {
    return this.createMeshBasket(basket);
  }

  visitFlexiShelf(flexiShelf) {
    //const shelfMesh = this.createPanel(flexiShelf);
    //if (!flexiShelf.parent) {
    //  /* We want to showcase the the top of the shelf. Exactly what angles we choose here also
    //   * depends on what camera angles we set up and lighting positions/directions: */
    //  this.setMeshRotation(shelfMesh, { x: 150, y: 10, z: 0 });
    //}
    //return shelfMesh;
    return this.createPanel(flexiShelf);
  }

  visitTopShelf(topShelf) {
    const shelfMesh = this.createPanel(topShelf);
    if (!topShelf.parent) {
      /* We want to showcase the the top of the shelf. Exactly what angles we choose here also
       * depends on what camera angles we set up and lighting positions/directions: */
      this.setMeshRotation(shelfMesh, { x: 150, y: 10, z: 0 });
    }
    return shelfMesh;
  }

  visitFlexiSectionContents(contents) {
    const contentsMesh = this.getContainerMeshAndPushContext(contents, null, true);
    contents.components.forEach(content => {
      return this.visitAndUpdatePosition(content);
    });
    this.popContext();
    return contentsMesh;
  }

  visitFlexiSection(section) {
    const sectionMesh = this.createFloorModule(section, (sction, sctionMesh) => this.visitAndUpdatePosition(sction.contents));
    return sectionMesh;
  }

  visitHangRodModule(module) {
    // The hangrod module is a standard module, typically within an organizer:
    const moduleMesh = this.createStandardModule(module, (mdule, contentsMesh) => {
      mdule.hangRods.forEach(rod => this.visitAndUpdatePosition(rod));
      mdule.supportRods.forEach(rod => this.visitAndUpdatePosition(rod));
      mdule.shelves.forEach(shelf => this.visitAndUpdatePosition(shelf));
    });
    return moduleMesh;
  }

  visitTopShelfModule(module) {
    const moduleMesh = this.getContainerMeshAndPushContext(module, null, true);
    module.components.forEach(shelf => {
      return this.visitAndUpdatePosition(shelf);
    });
    this.popContext();
    return moduleMesh;
  }

  visitClosetOrganizer(organizer) {
    // The organizer is a standard module, possibly within another organizer:
    const organizerMesh = this.createStandardModule(organizer, org => this.visitAndUpdatePosition(org.topShelfModule));
    return organizerMesh;
  }

  //#endregion *** Main Visitation Methods ***
}

/** Used when we're first downloading low-res images, and later downloading hi-res images. */
export class BabylonUpdateMaterialImageVisitor extends UpdateMaterialImageVisitor {
  updateMaterialImage(component, mesh, needsImageMaterial) {
    /* await */ BabylonRenderVisitor.replaceMeshMaterialStatic(component, null, mesh, null, needsImageMaterial);
  }

  visitFlexiBasketAndTracks(component) {
    if (component.displayObject) {
      // The basket has a inner mesh which is the one with the material:
      const wireMeshes = component.displayObject.getChildMeshes(true);
      this.updateMaterialImage(component, wireMeshes[0]);
    }
  }

  visitMeshBasketAndTracks(component) {
    if (component.displayObject) {
      /* The basket's first child mesh is the one with the "material". In this case the material
       * is a png image with a transparent mesh pattern. The second child mesh is the basket's rim,
       * and this mesh just needs a color material. */
      const childMeshes = component.displayObject.getChildMeshes(true);
      this.updateMaterialImage(component, childMeshes[0], /* needsImageMaterial: */ true); // Basket
      this.updateMaterialImage(component, childMeshes[1]); // Rim
    }
  }

}
